import { useEffect, useRef, useState } from "react";
import type { CocktailQrConfig, CocktailQrOption } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

type Stage = "scan" | "mslogin" | "decision";

/**
 * "The Free Cocktail QR Code" — QR phishing + credential theft, scene 2 of
 * the walkable-bar course. Three device beats drive three `stage`s:
 *   scan     -> viewfinder over the bait sticker, a bartender aside plays
 *               underneath, then the "open this site?" sheet.
 *   mslogin  -> fake "sign in with your company Microsoft account" page +
 *               Tom's hushed inner monologue.
 *   decision -> the 5-option decision list (this stage owns its OWN internal
 *               sub-flow: list -> optional sticker-peel -> recap/"what would
 *               have happened" reveal -> onAdvance(outcomeKey)).
 * Mirrors `QuishingPhone`'s lock-screen/viewfinder/sheet chrome so both QR
 * experiences read as the same "phone" across courses.
 */
export function CocktailQrPhone({
  config,
  stage,
  onOpenSite,
  onMsContinue,
  onChoose,
  onAdvance,
}: {
  config: CocktailQrConfig;
  stage: Stage;
  onOpenSite: () => void;
  onMsContinue: () => void;
  onChoose: (opt: CocktailQrOption) => void;
  /** stage !== "decision" -> called with no key (sim.advance()); the
   *  decision stage always calls it with the chosen option's outcome key. */
  onAdvance: (outcomeKey?: string) => void;
}) {
  return (
    <div className="cqr">
      <div className="cqr-stagewrap">
        {stage === "scan" && (
          <ScanStage config={config} onOpenSite={onOpenSite} onAdvance={onAdvance} />
        )}
        {stage === "mslogin" && (
          <MsLoginStage config={config} onMsContinue={onMsContinue} onAdvance={onAdvance} />
        )}
        {stage === "decision" && (
          <DecisionStage config={config} onChoose={onChoose} onAdvance={onAdvance} />
        )}
      </div>
    </div>
  );
}

function PhoneFrame({ clock, children }: { clock?: string; children: React.ReactNode }) {
  return (
    <div className="quish-phone">
      <div className="quish-notch" />
      <div className="quish-statusbar">
        <span>{clock ?? tx("9:41 PM")}</span>
        <span className="quish-sb-icons">
          <span>{"\u{1F4F6}"}</span>
          <span>{"\u{1F50B}"}</span>
        </span>
      </div>
      <div className="quish-screen cqr-screen">{children}</div>
    </div>
  );
}

/** Stage 1 — camera viewfinder over the "FREE COCKTAIL" sticker, then the
 *  "open this website?" sheet. A bartender aside plays underneath the sheet
 *  ("that's not mine"), which Tom dismisses out loud — heard, not read. */
function ScanStage({
  config,
  onOpenSite,
  onAdvance,
}: {
  config: CocktailQrConfig;
  onOpenSite: () => void;
  onAdvance: (outcomeKey?: string) => void;
}) {
  const [scanned, setScanned] = useState(false);
  const [opening, setOpening] = useState(false);
  const [asideShown, setAsideShown] = useState(0); // 0=none 1=bartender 2=+tom reply
  const mounted = useRef(true);
  const started = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (scanned || opening) return;
    const t = window.setTimeout(() => mounted.current && setScanned(true), 1600);
    return () => window.clearTimeout(t);
  }, [scanned, opening]);

  // The off-screen bar exchange plays once, timed just after the scan sheet appears.
  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    {
      const vlang = config.language ?? "en-US";
      if (config.thirstyLine) void audio.prefetchSpeech(config.thirstyLine, { voice: config.voiceId ?? "verse", lang: vlang });
      if (config.bartenderLine) {
        void audio.prefetchSpeech(config.bartenderLine, {
          voice: config.bartenderVoiceId ?? "onyx",
          lang: vlang,
          instructions: "A bartender calling out over the noise from a few steps away.",
        });
      }
      if (config.ignoreLine) void audio.prefetchSpeech(config.ignoreLine, { voice: config.voiceId ?? "verse", lang: vlang });
      if (config.msLogin?.innerVoiceLine) {
        void audio.prefetchSpeech(config.msLogin.innerVoiceLine, {
          voice: config.voiceId ?? "verse",
          lang: vlang,
          instructions: "Hushed, half-convincing himself, thinking out loud under his breath.",
        });
      }
    }
    if (started.current) return;
    started.current = true;
    let cancelled = false;
    (async () => {
      const lang = config.language ?? "en-US";
      if (config.thirstyLine) {
        await audio.speak(config.thirstyLine, { voice: config.voiceId ?? "verse", lang });
      }
      await new Promise((r) => window.setTimeout(r, 900));
      if (cancelled || !mounted.current) return;
      if (config.bartenderLine) {
        setAsideShown(1);
        await audio.speak(config.bartenderLine, {
          voice: config.bartenderVoiceId ?? "onyx",
          lang,
          instructions: "A bartender calling out over the noise from a few steps away.",
        });
      }
      if (cancelled || !mounted.current) return;
      await new Promise((r) => window.setTimeout(r, 350));
      if (config.ignoreLine) {
        setAsideShown(2);
        await audio.speak(config.ignoreLine, { voice: config.voiceId ?? "verse", lang });
      }
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = () => {
    audio.click();
    onOpenSite();
    setOpening(true);
    window.setTimeout(() => mounted.current && onAdvance(), 1100);
  };

  return (
    <PhoneFrame clock={config.lockClock}>
      <div className="quish-cam cqr-cam">
        <div className="quish-cam-feed">
          <div className="quish-qr" aria-hidden>
            <CocktailQrGlyph />
          </div>
          <div className={`quish-reticle ${scanned ? "locked" : ""}`}>
            <span className="r-tl" />
            <span className="r-tr" />
            <span className="r-bl" />
            <span className="r-br" />
            {!scanned && <span className="quish-scanbar" />}
          </div>
          <div className="quish-cam-hint">{scanned ? tx("QR code detected") : tx("Point at the sign…")}</div>
        </div>

        {asideShown > 0 && (
          <div className="cqr-aside">
            {asideShown >= 1 && (
              <div className="cqr-aside-line cqr-aside--bartender">
                <span className="cqr-aside-name">{tx("Bartender (across the bar)")}</span>
                {config.bartenderLine}
              </div>
            )}
            {asideShown >= 2 && (
              <div className="cqr-aside-line cqr-aside--tom">
                <span className="cqr-aside-name">{tx("Tom")}</span>
                {config.ignoreLine}
              </div>
            )}
          </div>
        )}

        {opening && <div className="quish-loading">{tx("Opening site…")}</div>}

        {scanned && !opening && (
          <div className="quish-sheet">
            <div className="quish-sheet-h">{tx("Open this website?")}</div>
            <div className="quish-url-big">{config.openPrompt.host}</div>
            <div className="quish-url-full">{config.openPrompt.full}</div>
            <div className="quish-sheet-row">
              <button className="quish-sbtn ghost cqr-sheet-cancel--grey" onClick={() => setScanned(false)}>
                {tx("Cancel")}
              </button>
              <div className="cqr-open-wrap">
                <button className="quish-sbtn primary cf-blink-strong" onClick={open}>
                  {tx("Open")}
                </button>
                <span className="pc-hint-chip cqr-open-hint">{tx("Click here")}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}

/** Stage 2 — a fake "sign in with your company Microsoft account" page,
 *  plus Tom's hushed inner monologue and an on-screen nudge to the learner. */
function MsLoginStage({
  config,
  onMsContinue,
  onAdvance,
}: {
  config: CocktailQrConfig;
  onMsContinue: () => void;
  onAdvance: (outcomeKey?: string) => void;
}) {
  const ml = config.msLogin;
  const [ready, setReady] = useState(false);
  // "no" -> show the reassuring "good choice" panel with See-what-if / Skip.
  const [declined, setDeclined] = useState(false);
  const started = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    onMsContinue();
    let cancelled = false;
    (async () => {
      await new Promise((r) => window.setTimeout(r, 500));
      if (cancelled || !mounted.current) return;
      await audio.speak(ml.innerVoiceLine, {
        voice: config.voiceId ?? "verse",
        lang: config.language ?? "en-US",
        instructions: "Hushed, half-convincing himself, thinking out loud under his breath.",
      });
      if (cancelled || !mounted.current) return;
      setReady(true);
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PhoneFrame clock={config.lockClock}>
      <div className="cqr-mslogin">
        <div className="cqr-mslogin-scroll">
          <div className="quish-addr">
            <span className="cqr-lock-warn" aria-hidden>
              {"\u26A0"}
            </span>
            <span className="quish-addr-url">{config.openPrompt.host}</span>
          </div>
          <div className="cqr-ms-card">
            <div className="cqr-ms-logo" aria-hidden>
              <span className="cqr-ms-tile cqr-ms-tile--r" />
              <span className="cqr-ms-tile cqr-ms-tile--g" />
              <span className="cqr-ms-tile cqr-ms-tile--b" />
              <span className="cqr-ms-tile cqr-ms-tile--y" />
            </div>
            <div className="cqr-ms-brand">{ml.brand ?? tx("Microsoft")}</div>
            <div className="cqr-ms-head">{tx("Sign in with your company account to claim your free cocktail")}</div>
            {ml.org && <div className="cqr-ms-org">{ml.org}</div>}
            <div className="cqr-ms-field">
              <label>{tx("Email, phone, or Skype")}</label>
              <input type="text" readOnly value={ml.userEmail} className="cf-mask" spellCheck={false} />
            </div>
            <div className="cqr-ms-field">
              <label>{tx("Password")}</label>
              <input
                type="text"
                readOnly
                value="••••••••••••"
                className="cf-mask"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                spellCheck={false}
              />
            </div>
            <button className="cqr-ms-signin" disabled>
              {tx("Sign in")}
            </button>
          </div>

          <div className="cqr-innervoice">
            <span className="cqr-innervoice-tag">{tx("Tom (to himself)")}</span>
            {ml.innerVoiceLine}
          </div>
          {ml.nudge && <div className="cqr-nudge">{ml.nudge}</div>}
        </div>

        {!declined ? (
          <div className="cqr-decide">
            <button
              type="button"
              className="btn cqr-decide-yes"
              disabled={!ready}
              onClick={() => onAdvance("qr_yes")}
            >
              {ready ? ml.yesLabel ?? tx("Yes — sign in with my work account") : "\u2026"}
            </button>
            <button
              type="button"
              className="btn btn-ghost cqr-decide-no"
              disabled={!ready}
              onClick={() => {
                audio.chimeGood();
                setDeclined(true);
              }}
            >
              {ml.noLabel ?? tx("No — this doesn't feel right")}
            </button>
          </div>
        ) : (
          <div className="cqr-goodchoice" role="status">
            <div className="cqr-goodchoice-badge">{tx("Good choice")}</div>
            <div className="cqr-goodchoice-body">
              {ml.goodChoiceBody ??
                tx("Never hand a work login to a page a QR sticker sent you to. Here's what you just avoided.")}
            </div>
            <div className="cqr-goodchoice-actions">
              <button type="button" className="btn cqr-decide-yes" onClick={() => onAdvance("qr_no_curious")}>
                {ml.seeWhatIfLabel ?? tx("See what would have happened")}
              </button>
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}

type DecisionPhase = "list" | "peel" | "recap";
type WhatIfTab = "bad" | "safe";

/** Stage 3 — the 5-option decision list, the sticker-peel branch, and the
 *  recap/"what would have happened" reveal, all owned internally so no
 *  extra beats/edges are needed for the sub-interactions. */
function DecisionStage({
  config,
  onChoose,
  onAdvance,
}: {
  config: CocktailQrConfig;
  onChoose: (opt: CocktailQrOption) => void;
  onAdvance: (outcomeKey?: string) => void;
}) {
  const [phase, setPhase] = useState<DecisionPhase>("list");
  const [chosen, setChosen] = useState<CocktailQrOption | null>(null);
  const [peeled, setPeeled] = useState(false);
  const [whatIf, setWhatIf] = useState<WhatIfTab | null>(null);
  const [revealIdx, setRevealIdx] = useState(0);
  const revealTimer = useRef<number | null>(null);

  const pick = (opt: CocktailQrOption) => {
    audio.click();
    setChosen(opt);
    onChoose(opt);
    if (opt.requiresPeel) {
      setPhase("peel");
    } else {
      opt.safe ? audio.chimeGood() : audio.chimeBad();
      setPhase("recap");
    }
  };

  const peel = () => {
    if (peeled) return;
    audio.click();
    setPeeled(true);
    window.setTimeout(() => {
      audio.chimeGood();
      if (chosen) setPhase("recap");
    }, 900);
  };

  const openWhatIf = (tab: WhatIfTab) => {
    setWhatIf(tab);
    setRevealIdx(0);
    const steps = tab === "bad" ? config.reveal.badSteps : config.reveal.safeSteps;
    if (revealTimer.current) window.clearInterval(revealTimer.current);
    let i = 0;
    revealTimer.current = window.setInterval(() => {
      i++;
      if (i >= steps.length) {
        if (revealTimer.current) window.clearInterval(revealTimer.current);
      }
      setRevealIdx(Math.min(i, steps.length - 1));
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (revealTimer.current) window.clearInterval(revealTimer.current);
    };
  }, []);

  if (phase === "list") {
    return (
      <div className="cqr-decision">
        <div className="cqr-decision-h">{config.decision.prompt ?? tx("What do you do?")}</div>
        <div className="cqr-decision-list">
          {config.decision.options.map((o) => (
            <button key={o.id} type="button" className="cqr-opt" onClick={() => pick(o)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (phase === "peel" && chosen) {
    return (
      <div className="cqr-peel-wrap">
        <div className="cqr-peel-h">{config.peel.instructionLabel ?? tx("Take a closer look at the sticker.")}</div>
        <div className={`cqr-peel-stage ${peeled ? "peeled" : ""}`} onClick={peel} role="button" tabIndex={0}>
          <img className="cqr-peel-under" src={config.peel.legitImageUrl} alt={tx("The real QR code underneath")} />
          <img className="cqr-peel-top" src={config.peel.stickerImageUrl} alt={tx("The bait sticker")} />
          {!peeled && <div className="cqr-peel-hint">{tx("Tap to peel back the sticker")}</div>}
        </div>
        {peeled && (
          <div className="cqr-peel-result">
            <div className="cqr-peel-result-title">{config.peel.revealedLabel ?? tx("There's another QR code underneath.")}</div>
            {config.peel.revealLine && <div className="cqr-peel-result-body">{config.peel.revealLine}</div>}
          </div>
        )}
      </div>
    );
  }

  if (phase === "recap" && chosen) {
    return (
      <div className="cqr-recap">
        <div className={`cqr-recap-badge ${chosen.safe ? "safe" : "bad"}`}>
          {chosen.safe ? tx("Good call") : tx("Risky move")}
        </div>
        <div className="cqr-recap-line">{chosen.resultLine}</div>

        <button type="button" className="cqr-whatif-btn" onClick={() => openWhatIf(chosen.safe ? "bad" : "safe")}>
          {config.reveal.whatIfLabel ?? tx("What would have happened instead?")}
        </button>

        {whatIf && (
          <div className="cqr-whatif-modal" role="dialog" aria-label={tx("What would have happened")}>
            <div className="cqr-whatif-tabs">
              <button
                type="button"
                className={whatIf === "bad" ? "on" : ""}
                onClick={() => openWhatIf("bad")}
              >
                {config.reveal.badTitle ?? tx("If credentials were entered")}
              </button>
              <button
                type="button"
                className={whatIf === "safe" ? "on" : ""}
                onClick={() => openWhatIf("safe")}
              >
                {config.reveal.safeTitle ?? tx("If you stayed safe")}
              </button>
            </div>
            <ul className="cqr-whatif-steps">
              {(whatIf === "bad" ? config.reveal.badSteps : config.reveal.safeSteps).map((s, i) => (
                <li key={i} className={i === revealIdx ? "active" : i < revealIdx ? "past" : ""}>
                  <span className="cqr-whatif-dot" />
                  {s}
                </li>
              ))}
            </ul>
            <button type="button" className="quish-sbtn ghost cqr-whatif-close" onClick={() => setWhatIf(null)}>
              {tx("Close")}
            </button>
          </div>
        )}

        <button type="button" className="btn cqr-continue" onClick={() => onAdvance(chosen.outcome)}>
          {config.reveal.continueLabel ?? tx("Continue")}
        </button>
      </div>
    );
  }

  return null;
}

/** A cocktail-glass-marked QR glyph for the viewfinder (decorative, distinct
 *  from `QuishingPhone`'s plain glyph so the two scenes don't feel identical). */
function CocktailQrGlyph() {
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden>
      <rect x="0" y="0" width="100" height="100" fill="#fff" />
      <g fill="#111">
        <rect x="6" y="6" width="22" height="22" />
        <rect x="72" y="6" width="22" height="22" />
        <rect x="6" y="72" width="22" height="22" />
        <rect x="12" y="12" width="10" height="10" fill="#fff" />
        <rect x="78" y="12" width="10" height="10" fill="#fff" />
        <rect x="12" y="78" width="10" height="10" fill="#fff" />
        {Array.from({ length: 11 }).map((_, r) =>
          Array.from({ length: 11 }).map((_, c) => {
            if ((r < 3 && c < 3) || (r < 3 && c > 7) || (r > 7 && c < 3)) return null;
            return (r * 5 + c * 7) % 5 < 2 ? (
              <rect key={`${r}-${c}`} x={6 + c * 8} y={6 + r * 8} width="6" height="6" />
            ) : null;
          })
        )}
      </g>
    </svg>
  );
}
